import {
  ProposalSource,
  ProposalStatus,
  ProposalSubType,
  type Proposal,
  type ProposalBulkCreateRequest,
  type ProposalPatchRequest,
  type ProposalStatus as ProposalStatusValue,
  type ProposalSubType as ProposalSubTypeValue,
} from '@/types/proposals';
import { z } from 'zod';

export const PROPOSAL_SUB_TYPE_LABELS: Record<ProposalSubTypeValue, string> = {
  [ProposalSubType.BALANCE_DUE]: 'Balance due',
  [ProposalSubType.UNFILED_RETURN]: 'Backfiling',
};

export const PROPOSAL_STATUS_LABELS: Record<ProposalStatusValue, string> = {
  [ProposalStatus.DRAFT]: 'Draft',
  [ProposalStatus.AWAITING_CUSTOMER]: 'Awaiting customer',
  [ProposalStatus.APPROVED]: 'Accepted',
  [ProposalStatus.DECLINED]: 'Declined',
};

/** Ark Select rejects an empty item value, so the form stores '' and the control uses this. */
export const PROPOSAL_UNASSIGNED = 'UNASSIGNED';

/** Balance-due month choice that clears an optional period. Not a real month. */
export const PROPOSAL_NO_PERIOD = 'none';

export const PROPOSAL_MONTH_OPTIONS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
] as const;

const MONTH_LABELS = PROPOSAL_MONTH_OPTIONS.map(option => option.label);

const optionalDecimal = z.string().refine(value => {
  const trimmed = value.trim();
  if (!trimmed) return true;
  return /^\d+(\.\d{1,2})?$/.test(trimmed);
}, 'Enter a dollar amount');

export const proposalPeriodItemSchema = z.object({
  periodMonth: z.string(),
  periodYear: z.string(),
  amount: optionalDecimal,
});

export const proposalStateRowSchema = z.object({
  stateCode: z.string().min(1, 'State is required'),
  detail: z.string(),
  assignee: z.string(),
  items: z.array(proposalPeriodItemSchema).min(1),
});

export const createProposalFormSchema = z
  .object({
    subType: z.enum([ProposalSubType.BALANCE_DUE, ProposalSubType.UNFILED_RETURN]),
    assignee: z.string(),
    rows: z.array(proposalStateRowSchema).min(1, 'Add at least one state'),
  })
  .superRefine((value, ctx) => {
    const seenStates = new Set<string>();
    value.rows.forEach((row, rowIndex) => {
      if (row.stateCode) {
        if (seenStates.has(row.stateCode)) {
          ctx.addIssue({
            code: 'custom',
            path: ['rows', rowIndex, 'stateCode'],
            message: 'This state is already listed. Add another period on that state.',
          });
        }
        seenStates.add(row.stateCode);
      }
      row.items.forEach((item, itemIndex) => {
        validatePeriodAndAmount(value.subType, item, ['rows', rowIndex, 'items', itemIndex], ctx);
      });
    });
  });

export type CreateProposalFormValues = z.infer<typeof createProposalFormSchema>;
export type ProposalPeriodItemValues = z.infer<typeof proposalPeriodItemSchema>;

export const proposalEditItemSchema = proposalPeriodItemSchema.extend({
  id: z.string().min(1),
});

export const proposalEditSchema = z
  .object({
    subType: z.enum([ProposalSubType.BALANCE_DUE, ProposalSubType.UNFILED_RETURN]),
    detail: z.string(),
    assignee: z.string(),
    items: z.array(proposalEditItemSchema).min(1),
  })
  .superRefine((value, ctx) => {
    value.items.forEach((item, itemIndex) => {
      validatePeriodAndAmount(value.subType, item, ['items', itemIndex], ctx);
    });
  });

export type ProposalEditValues = z.infer<typeof proposalEditSchema>;

function validatePeriodAndAmount(
  subType: ProposalSubTypeValue,
  item: ProposalPeriodItemValues,
  pathPrefix: (string | number)[],
  ctx: z.RefinementCtx
) {
  const month = item.periodMonth.trim();
  const year = item.periodYear.trim();
  const amount = item.amount.trim();
  const hasMonth = month !== '';
  const hasYear = year !== '';

  if (hasMonth !== hasYear) {
    ctx.addIssue({
      code: 'custom',
      path: [...pathPrefix, hasMonth ? 'periodYear' : 'periodMonth'],
      message: 'Enter both the month and the year',
    });
  }
  if (hasYear && !/^\d{4}$/.test(year)) {
    ctx.addIssue({
      code: 'custom',
      path: [...pathPrefix, 'periodYear'],
      message: 'Enter a four-digit year',
    });
  }
  if (hasMonth) {
    const monthNumber = Number(month);
    if (!Number.isInteger(monthNumber) || monthNumber < 1 || monthNumber > 12) {
      ctx.addIssue({
        code: 'custom',
        path: [...pathPrefix, 'periodMonth'],
        message: 'Period month is invalid',
      });
    }
  }
  if (subType === ProposalSubType.UNFILED_RETURN && (!hasMonth || !hasYear)) {
    ctx.addIssue({
      code: 'custom',
      path: [...pathPrefix, 'periodMonth'],
      message: 'Period is required for a backfiling',
    });
  }
  if (subType === ProposalSubType.BALANCE_DUE && !amount) {
    ctx.addIssue({
      code: 'custom',
      path: [...pathPrefix, 'amount'],
      message: 'Amount is required for a balance due',
    });
  }
}

export function emptyProposalItem(): ProposalPeriodItemValues {
  return { periodMonth: '', periodYear: '', amount: '' };
}

export function emptyProposalRow(assignee = ''): CreateProposalFormValues['rows'][number] {
  return { stateCode: '', detail: '', assignee, items: [emptyProposalItem()] };
}

export function defaultCreateProposalForm(assignee = ''): CreateProposalFormValues {
  return {
    subType: ProposalSubType.BALANCE_DUE,
    assignee,
    rows: [emptyProposalRow(assignee)],
  };
}

function periodPart(value: string): number | null {
  const trimmed = value.trim();
  return trimmed ? Number(trimmed) : null;
}

function amountPart(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

export function buildProposalBulkCreate(values: CreateProposalFormValues): ProposalBulkCreateRequest {
  return {
    proposals: values.rows.map(row => ({
      sub_type: values.subType,
      source: ProposalSource.TAX_OPS,
      detail: row.detail.trim(),
      // The header assignee is copied onto each row when it changes. An empty
      // row is an explicit clear and must not fall back to the header.
      assignee: row.assignee.trim() ? row.assignee.trim() : null,
      items: row.items.map(item => ({
        state_code: row.stateCode,
        period_year: periodPart(item.periodYear),
        period_month: periodPart(item.periodMonth),
        amount: amountPart(item.amount),
      })),
    })),
  };
}

export function proposalToEditValues(proposal: Proposal): ProposalEditValues {
  return {
    subType: proposal.sub_type,
    detail: proposal.detail,
    assignee: proposal.assignee ?? '',
    items: proposal.items.map(item => ({
      id: item.id,
      periodMonth: item.period_month == null ? '' : String(item.period_month),
      periodYear: item.period_year == null ? '' : String(item.period_year),
      amount: item.amount ?? '',
    })),
  };
}

export function buildProposalPatch(values: ProposalEditValues): ProposalPatchRequest {
  return {
    detail: values.detail.trim(),
    assignee: values.assignee.trim() ? values.assignee.trim() : null,
    items: values.items.map(item => ({
      id: item.id,
      period_year: periodPart(item.periodYear),
      period_month: periodPart(item.periodMonth),
      amount: amountPart(item.amount),
    })),
  };
}

export function isProposalEditable(status: ProposalStatusValue): boolean {
  return status === ProposalStatus.DRAFT || status === ProposalStatus.AWAITING_CUSTOMER;
}

export function formatProposalPeriod(year: number | null, month: number | null): string {
  if (year == null || month == null) return '';
  const label = MONTH_LABELS[month - 1];
  return label ? `${label} ${year}` : '';
}

export function formatProposalAmount(amount: string | null): string {
  if (!amount) return '';
  const value = Number(amount);
  if (Number.isNaN(value)) return amount;
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}
